/**
 * Joining (WALLIYO-0131): who is on their way in.
 *
 * Two lists, read from the one staff list: invitations nobody has accepted,
 * and people who arrived recently. An invitation that has been out a week says
 * so on its own row, because the thing to do about it is to talk to the
 * person, and that only happens if the row says so.
 */

import type { StaffMemberListItem, StaffRoleListItem } from "@wallyo/contracts/console";
import { t } from "../../i18n";
import { formatDateTime } from "../../i18n/format";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { daysWaiting, isStale, joining } from "./joining-model";

export type JoiningState =
  | { status: "loading"; staff: readonly StaffMemberListItem[] | null; roles: readonly StaffRoleListItem[] }
  | { status: "ready"; staff: readonly StaffMemberListItem[]; roles: readonly StaffRoleListItem[] }
  | {
      status: "failed";
      staff: readonly StaffMemberListItem[] | null;
      roles: readonly StaffRoleListItem[];
      error: unknown;
    };

function Roles({ member }: { member: StaffMemberListItem }) {
  if (member.roles.length === 0) return <span className="foot">{t("directory.noRole")}</span>;
  return <>{member.roles.map((role) => role.name).join(", ")}</>;
}

/** How long it has been out, said the way somebody would say it aloud. */
function Waiting({ member, now }: { member: StaffMemberListItem; now: Date }) {
  const days = daysWaiting(member, now);
  if (days === 0) return <>{t("joining.today")}</>;
  return <>{t("joining.waiting", { days: String(days) })}</>;
}

export function JoiningView(props: { state: JoiningState; now: Date; onInvite: () => void; onRetry: () => void }) {
  const { state, now } = props;
  const staff = state.staff;
  const lists = staff ? joining(staff, now) : null;

  return (
    <>
      <div className="btns">
        <button className="btn" type="button" onClick={props.onInvite} disabled={state.status !== "ready"}>
          {t("joining.invite")}
        </button>
      </div>

      {state.status === "failed" ? (
        <div className="audit-error">
          <p className="foot">{t("hr.failed")}</p>
          <ErrorNotice error={state.error} />
          <div className="btns">
            <button className="btn" type="button" onClick={props.onRetry}>
              {t("hr.retry")}
            </button>
          </div>
        </div>
      ) : null}

      {!lists && state.status === "loading" ? (
        <div className="empty" role="status">
          <span className="spinner" aria-hidden="true" />
          <span>{t("hr.loading")}</span>
        </div>
      ) : null}

      {lists ? (
        <>
          <section className="card">
            <h2>{t("joining.invited.title")}</h2>
            <p className="foot">{t("joining.invited.help")}</p>
            {lists.invited.length === 0 ? (
              <p className="foot">{t("joining.invited.none")}</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t("joining.col.name")}</th>
                      <th>{t("joining.col.roles")}</th>
                      <th>{t("joining.col.sent")}</th>
                      <th>{t("joining.col.waiting")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lists.invited.map((member) => (
                      <tr key={member.staffMemberId}>
                        <td>
                          <strong>{member.name}</strong>
                          <div className="foot">{member.email}</div>
                        </td>
                        <td>
                          <Roles member={member} />
                        </td>
                        <td>{formatDateTime(member.createdAt)}</td>
                        <td>
                          <Waiting member={member} now={now} />
                          {isStale(member, now) ? (
                            <div>
                              <span className="pill warn">{t("joining.stale")}</span>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="card">
            <h2>{t("joining.arrived.title")}</h2>
            <p className="foot">{t("joining.arrived.help")}</p>
            {lists.arrived.length === 0 ? (
              <p className="foot">{t("joining.arrived.none")}</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t("joining.col.name")}</th>
                      <th>{t("joining.col.roles")}</th>
                      <th>{t("joining.col.joined")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lists.arrived.map((member) => (
                      <tr key={member.staffMemberId}>
                        <td>
                          <strong>{member.name}</strong>
                          <div className="foot">{member.email}</div>
                        </td>
                        <td>
                          <Roles member={member} />
                        </td>
                        <td>{formatDateTime(member.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : null}
    </>
  );
}
